import { Box, Button, Container } from "@material-ui/core";
import { CloseOutlined } from "@material-ui/icons";
import { Formik } from "formik";
import React, { Dispatch, useEffect } from "react";
import { Translation, useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { without } from "underscore";
import useAppContext from "../../context/hooks/useAppContext";
import useFleetManagerContext from "../../context/hooks/useFleetManagerContext";
import useImportVehiclesContext from "../../context/hooks/useImportVehiclesContext";
import parkingProductsActions from "../../store/actions/parkingProducts.actions";
import { RootReducer } from "../../store/reducers";
import { variables } from "../../theme/variables";
import CustomButton from "../../ui/CustomButton/CustomButton";
import Heading from "../../ui/Heading/Heading";
import ParkingRightsGrid from "../DelegationForm/ParkingRightsGrid/ParkingRightsGrid";
import VehicleConcurrentUploadWarning, { useBlockVehicleConcurrentUpload } from "../VehicleConcurrentUploadWarning";
import "./_multipleVehiclesForm.scss";
import useValidationSchema, { IWhitelistVehiclesFormValues } from "./hooks/useValidationSchema";
import * as parkingProductsThunks from "../../store/actions/parkingProducts.thunks";
const { typography } = variables;
interface IFormProps extends StateProps, DispatchProps {
  onClose: () => void;
  onRefresh?: () => void;
}

enum Fields {
  Products = "products"
}

const MultipleVehiclesForm: React.FC<IFormProps> = (props) => {
  const {
    onClose,
    parkingProducts,
    getUnlimitedParkingProducts,
    disposeError,
  } = props;

  const { appState } = useAppContext();
  const { setVehicleDragAndDropView } =
  useFleetManagerContext();
  const {
    setSelectedProducts,
  } = useImportVehiclesContext();

  const blockConcurrentUpload = useBlockVehicleConcurrentUpload();
  const { t } = useTranslation(["fleetManagerTable"]);

  useEffect(() => {
    return () => disposeError();
  },  [disposeError]);

  const handleFormSubmit = (values: IWhitelistVehiclesFormValues) => {
   setSelectedProducts(values.products!.map(pmcId => ({ pmcId, activationDate: null, expirationDate: null })));
    goToUploadFile();
  };

  const { validationSchema, formik } = useValidationSchema(
    t,
    handleFormSubmit
  );

  const fetchParkingProducts = () => {
    getUnlimitedParkingProducts(appState.user.seasonTicketOwnerCrmId as string, appState.selectedLanguage);
  };

  const initForm = () => {
    fetchParkingProducts();
    blockConcurrentUpload.fetch();
  };

  useEffect(() => {
    initForm();
  }, []);

  const handleSelectAll = (parkingProducts: number[]) => {
    formik.setFieldValue(Fields.Products, parkingProducts);
  };

  const handleDeselectAll = () => {
    formik.setFieldValue(Fields.Products, []);
  };

  const handleProductChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    parkingProducts: number[]
  ) => {
    const { value, checked } = event.currentTarget;
    const productId = parseInt(value);

    let products = [...parkingProducts];
    if (!checked) {
      products = without(products, productId);
    } else {
      products.push(productId);
    }
    formik.setFieldValue(Fields.Products, products);
  };

  const getFormTitle = () => t("whitelistVehicles");
  const submitButtonText = () => t("delegeeForm:goToUpload")
  const goToUploadFile = (): void => setVehicleDragAndDropView(true);

  return (
    <Formik
      validationSchema={validationSchema}
      initialValues={formik.initialValues}
      onSubmit={handleFormSubmit}
    >
        <div className="form-container">
          <div className="form-header">
            <Container className="form-header-content">
              <Heading width="auto" justifyContent="center" fontSize={typography.fontSizeLarge}>
                {getFormTitle()}
              </Heading>
              <Button onClick={onClose} classes={{ root: "close-icon-btn" }} disableRipple>
                <CloseOutlined classes={{ root: "close-icon" }} />
              </Button>
            </Container>
          </div>
          <Box component="form" sx={{ flexGrow: 1, paddingY: 2 }}>
            <div className="form-body">
              <Container>
                <Translation ns={["vehicleForm", "delegeeForm"]} nsMode="fallback">
                  {(t) => (
                   <>
                    <ParkingRightsGrid
                      data={parkingProducts.parkingProducts.data!}
                      loading={parkingProducts.parkingProducts.loading}
                      onChange={(event) => handleProductChange(event, formik.values.products ?? [])}
                      onStateChange={prs => handleSelectAll(prs.filter(pr => pr.checked).map(pr => pr.right?.pmcId!))}
                      onSelectAll={(ids) => handleSelectAll(ids.map(id => id.pmcId!))}
                      onDeselectAll={handleDeselectAll}
                      error={{
                        hasError: Boolean(formik.errors.products),
                        message: formik.errors.products,
                      }}
                      title={t("selectProduct")}
                      required={true}
                      selectAllTooltipText={t("selectTooltip")}
                      deselectAllTooltipText={t("deselectTooltip")}
                      selectAllText={t("selectAll")}
                      deselectAllText={t("deselectAll")}
                    />

                  {blockConcurrentUpload.blocked && <VehicleConcurrentUploadWarning />}
                  </>
                  )}
                </Translation>

              </Container>
            </div>
          </Box>
          <div className="form-footer">
            <Container className="form-footer-content">
              <React.Fragment>
                <CustomButton.Light onClick={onClose} text={t("globals:close")} id="close-invite" />
                <CustomButton.Primary
                  loading={parkingProducts.loading}
                  onClick={(e) => {
                    e?.preventDefault();
                    formik.handleSubmit();
                  }}
                  text={submitButtonText()}
                  id="submit-invite"
                  disabled={blockConcurrentUpload.blocked}
                />
              </React.Fragment>
            </Container>
          </div>
        </div>
    </Formik>
  );
};

const mapStateToProps = (state: RootReducer) => {
  const { parkingProducts } = state;

  return {
    parkingProducts
  };
};

const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
  disposeError: () => dispatch(parkingProductsActions.disposeError()),
  getUnlimitedParkingProducts: (seasonTicketOwnerCrmId: string, location: string,) => dispatch(parkingProductsThunks.getParkingProducts({ seasonTicketOwnerCrmId, location, unlimitedOnly: true })),
});

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;

export default connect(mapStateToProps, mapDispatchToProps)(MultipleVehiclesForm);
